import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useTemplates from "../../../services/Whatsapp/useTemplates";
import useWhatsappNumbers from "../../../services/Whatsapp/useWhatsappNumbers";
import { Eye, RotateCcw, Trash } from "lucide-react";
import LoadingScreen from "../../../utils/LoadingScreen";
import NoIndex from "../../../components/NoIndex";

const TemplateStatus = () => {
  const navigate = useNavigate();

  const { templates, loading, fetchVendorTemplates, deleteVendorTemplate } =
    useTemplates();

  const { whatsappNumbers } = useWhatsappNumbers();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNumberIndex, setSelectedNumberIndex] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteTemplate, setSelectedDeleteTemplate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const selectedWhatsappNumber =
    selectedNumberIndex !== ""
      ? whatsappNumbers?.[Number(selectedNumberIndex)]
      : null;

  const selectedPhoneNumberId = selectedWhatsappNumber?.phone_no_id || "";

  const filteredTemplates = templates?.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleRefresh = async () => {
    if (!selectedPhoneNumberId) return;

    setRefreshing(true);

    const startTime = Date.now();

    await fetchVendorTemplates(selectedPhoneNumberId);

    const elapsed = Date.now() - startTime;
    const remainingTime = 5000 - elapsed;

    if (remainingTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingTime));
    }

    setRefreshing(false);
  };

  const openDeleteModal = (template) => {
    setSelectedDeleteTemplate(template);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;

    setDeleteModalOpen(false);
    setSelectedDeleteTemplate(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteTemplate?.name) return;

    try {
      setDeleting(true);

      const res = await deleteVendorTemplate(
        selectedDeleteTemplate.name,
        selectedPhoneNumberId,
      );

      if (res?.responseCode === "00") {
        toast.success("Template deleted successfully");
        closeDeleteModal();
      } else {
        toast.error(
          res?.response?.error?.error_user_msg ||
            res?.response?.error?.message ||
            res?.responseMessage ||
            "Failed to delete template",
        );
      }
    } catch (err) {
      toast.error("Something went wrong while deleting template");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (selectedPhoneNumberId) {
      fetchVendorTemplates(selectedPhoneNumberId);
    }
  }, [selectedPhoneNumberId, selectedNumberIndex]);

  useEffect(() => {
    if (whatsappNumbers?.length > 0 && selectedNumberIndex === "") {
      const firstValidIndex = whatsappNumbers.findIndex(
        (item) => item?.phone_no,
      );

      const defaultIndex = firstValidIndex !== -1 ? firstValidIndex : 0;

      setSelectedNumberIndex(String(defaultIndex));
    }
  }, [whatsappNumbers, selectedNumberIndex]);

  return (
    <>
      <NoIndex />
      <ToastContainer />
      <div className="p-8 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
        {/* Top Card */}
        <div className="bg-white rounded-xl shadow p-6 mb-8 border montserrat-font-1">
          <div className="flex justify-between items-center mb-6 montserrat-font-1">
            <h2 className="text-xl font-semibold">Template Status</h2>

            <div className="flex gap-3">
              <button
                onClick={() => navigate("/WhatsApp/Create-Template")}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2 rounded-md font-medium"
              >
                + Create New Template
              </button>

              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 border rounded-md px-3 py-2 bg-blue-500 text-white"
              >
                <RotateCcw strokeWidth={2.5} size={16} />
                Refresh
              </button>
            </div>
          </div>

          <hr className="mb-6" />

          {/* Filters */}
          <div className="flex gap-6 montserrat-font-1">
            <div>
              <label className="block text-sm font-medium mb-1">
                WhatsApp Number
              </label>
              <select
                value={selectedNumberIndex}
                onChange={(e) => {
                  setSelectedNumberIndex(e.target.value);
                }}
                className="border rounded-md px-4 py-2 w-72"
              >
                <option value="">Select WhatsApp Number</option>

                {whatsappNumbers?.map((num, index) => (
                  <option
                    key={`${num.phone_no_id}-${index}`}
                    value={String(index)}
                  >
                    {num.phone_no_id} - ({num.phone_no || "No Number"})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Search Template
              </label>
              <input
                type="text"
                placeholder="Search Template..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border rounded-md px-4 py-2 w-72"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="bg-white rounded-xl shadow overflow-auto h-full border montserrat-font-1">
          <table className="min-w-full text-sm text-center border border-gray-300 montserrat-font-1">
            <thead className="sticky top-0 z-10 bg-emerald-500 text-white">
              <tr>
                <th className="py-4 border border-gray-300">Template ID</th>
                <th className="py-4 border border-gray-300">Template Name</th>
                <th className="py-4 border border-gray-300">Language</th>
                <th className="py-4 border border-gray-300">Category</th>
                <th className="py-4 border border-gray-300">Status</th>
                <th className="py-4 border border-gray-300">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-10">
                    <div className="flex justify-center items-center">
                      <LoadingScreen text="Updating ..." />
                    </div>
                  </td>
                </tr>
              ) : filteredTemplates?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-8 text-gray-400 text-center">
                    No templates found
                  </td>
                </tr>
              ) : (
                filteredTemplates?.map((item, index) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50 transition"
                  >
                    <td className="border border-gray-300 py-4">{item.id}</td>
                    <td className="border border-gray-300 py-4">{item.name}</td>
                    <td className="border border-gray-300 py-4">
                      {item.language}
                    </td>
                    <td className="border border-gray-300 py-4">
                      {item.category}
                    </td>
                    <td className="border border-gray-300 py-4">
                      {item.status === "APPROVED" ? (
                        <span className="bg-green-100 text-green-600 px-4 py-1 rounded-full text-xs font-semibold">
                          Approved
                        </span>
                      ) : (
                        <span className="bg-red-100 text-red-600 px-4 py-1 rounded-full text-xs font-semibold">
                          {item.status}
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="flex justify-center gap-4 montserrat-font-1">
                        {/* View Button */}
                        <button
                          onClick={() =>
                            navigate(
                              `/WhatsApp/TemplateView?templateID=${item.id}&templateName=${item.name}`,
                            )
                          }
                          className="text-gray-500 hover:text-black transition"
                        >
                          <Eye size={22} />
                        </button>

                        {/* Delete Button */}
                        <button
                          onClick={() => openDeleteModal(item)}
                          className="bg-red-100 text-red-500 p-2 rounded-md hover:bg-red-200 transition"
                        >
                          <Trash size={22} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination (UI Only Example) */}
        <div className="flex justify-center mt-6 gap-2 montserrat-font-1 items-center">
          <button className="px-3 py-1 bg-gray-200 rounded">&lt;</button>
          <button className="px-3 py-1 bg-blue-500 text-white rounded">
            1
          </button>
          <button className="px-3 py-1 bg-gray-200 rounded">&gt;</button>
        </div>

        {deleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
              <div className="border-b px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Template
                </h3>
              </div>

              <div className="px-6 py-5">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this template?
                </p>

                <p className="mt-3 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-800">
                  {selectedDeleteTemplate?.name}
                </p>

                <p className="mt-3 text-xs text-red-500">
                  This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end gap-3 border-t px-6 py-4">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  disabled={deleting}
                  className="rounded-md border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="rounded-md bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-60"
                >
                  {deleting ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TemplateStatus;
